(() => {
  // ── Section stacking (sticky card-over-card effect) ─────────────────────────
  // Sections that opt in via `js-stack-section` in their schema "class" field
  // receive position:sticky + an auto-assigned z-index based on DOM order.
  // `js-stack-pin` additionally sets min-height:100vh (the "fixed" section).
  // Re-runs on every Shopify editor event so reordering is fully supported.
  function initSectionStack() {
    const main = document.getElementById('MainContent');
    if (!main) return;
    const isMobile = window.innerWidth < 750;
    // Mobile only: overlap the section after a self-sticky hero so carousel can
    // visually slide over the colored panel after it has had its own reveal phase.
    // Must match the extra 100vh added to the hero's .image-with-text height on mobile
    // (hero.liquid: calc(270vh + state.total) vs original calc(170vh + state.total)).
    // The carousel enters viewport at the moment the colored panel's stable sticky phase
    // begins (after cascade animation finishes at ~112vh), then takes exactly 100vh of
    // scroll to fully cover the colored panel before the hero exits the viewport.
    const MOBILE_FOLLOW_SECTION_OVERLAP_VH = 100;

    // Reset all sections first so a removed section doesn't keep stale styles.
    main.querySelectorAll('.shopify-section').forEach((s) => {
      s.style.position = '';
      s.style.top = '';
      s.style.zIndex = '';
      s.style.minHeight = '';
      s.style.marginTop = '';
      s.style.marginBottom = '';
    });
    // Remove any backdrop elements injected in a previous run.
    main.querySelectorAll('.js-stack-carousel-backdrop').forEach((el) => el.remove());
    // Restore any inner elements de-stickied in a previous run.
    main.querySelectorAll('.shopify-section.js-stack-self-sticky .image-with-text').forEach((el) => {
      el.style.position = '';
    });

    // heroOverlapApplied ensures the negative-margin overlap trick fires ONLY
    // for the carousel that immediately follows the hero, not for every section
    // that happens to follow any js-stack-self-sticky section (text-media-1,
    // feature, etc. also carry that class, which was causing their following
    // sections to get an unwanted -100vh margin and slide over them too early).
    let prevWasSelfSticky = false;
    let heroOverlapApplied = false;
    let mobileGapReduction = 0;
    main.querySelectorAll('.shopify-section.js-stack-section').forEach((section, index) => {
      section.style.position = 'sticky';
      section.style.top = '0';
      section.style.zIndex = String(index + 1);

      // On mobile, pull the section after the carousel up to close the white
      // gap below the collection cards. Uses negative margin-top so the section
      // enters the viewport sooner, plus a compensating positive margin-bottom
      // so total document height stays unchanged (preserving the footer reveal).
      let gapReductionApplied = false;
      if (isMobile && mobileGapReduction > 0) {
        const reduction = mobileGapReduction;
        mobileGapReduction = 0;
        section.style.marginTop = `-${reduction}px`;
        section.style.marginBottom = `${reduction}px`;
        gapReductionApplied = true;
      }

      if (section.classList.contains('js-stack-self-sticky')) {
        // This section has its own internal sticky element (the hero's .image-with-text
        // is position:sticky via CSS). Putting sticky on BOTH the outer wrapper and an
        // inner element creates a "frozen container" — the inner sticky loses its scroll
        // range because it lives inside a frozen outer sticky.
        //
        // Desktop: let the outer wrapper handle sticky stacking and de-sticky the inner
        // .image-with-text to avoid a double-sticky frozen container.
        // Mobile: keep hero outer wrapper non-sticky so its native internal sequence works:
        // video sticks first, then colored panel slides above it, then next section follows.
        //
        // Exception: if the section received gap-reduction (it's right after the
        // carousel), it must stay sticky for the stacking effect to work. Apply
        // the desktop treatment (de-sticky inner elements) so it sticks at top:0.
        if (!isMobile || gapReductionApplied) {
          const innerSticky = section.querySelector('.image-with-text');
          if (innerSticky) innerSticky.style.position = 'relative';
        } else {
          section.style.position = 'relative';
          section.style.top = '';
        }
      }

      if (section.classList.contains('js-stack-pin')) {
        section.style.minHeight = '100svh';
      }

      // On mobile, pull the carousel up so it enters the viewport while the hero's
      // colored panel is still pinned at the top (the hero has an extra 100vh of height
      // for this). The compensating margin-bottom cancels the layout shift so every
      // section AFTER the carousel starts at its natural document position — without
      // it, text-media-1, feature, etc. would also start 100vh too early and get
      // covered before their content is visible.
      // heroOverlapApplied prevents this from firing again for text-media-2 (after
      // text-media-1) or other sections that follow a js-stack-self-sticky section.
      if (isMobile && prevWasSelfSticky && !heroOverlapApplied) {
        section.style.marginTop = `-${MOBILE_FOLLOW_SECTION_OVERLAP_VH}svh`;
        section.style.marginBottom = `${MOBILE_FOLLOW_SECTION_OVERLAP_VH}svh`;

        // The colored panel is 100vh tall but the carousel's content is shorter.
        // Approaches tried and why they don't work:
        //   - min-height on .shopify-section: wrapper is transparent → no cover
        //   - min-height on first child color container: stretches the images
        //   - position:absolute backdrop: iOS Safari doesn't reliably treat a
        //     sticky element as a containing block for absolute children
        //
        // Correct fix: inject a position:sticky curtain as the first child of
        // the carousel section. The curtain has height:100vh and the carousel's
        // background color, and margin-bottom:-100vh so it doesn't push the
        // carousel content down. As the carousel scrolls up from the bottom of
        // the viewport, the curtain scrolls with it and covers the area of the
        // viewport where the colored panel would bleed through. Once the carousel
        // sticks at top:0, the curtain sticks too, covering the full viewport.
        // Carousel content (heading, images) follows the curtain in DOM order
        // and paints over it naturally — no layout or height changes.
        const colorEl = section.firstElementChild;
        let bgColor = colorEl ? getComputedStyle(colorEl).backgroundColor : '';
        const isOpaque = (c) => c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent';
        if (!isOpaque(bgColor)) bgColor = getComputedStyle(section).backgroundColor;
        if (!isOpaque(bgColor)) bgColor = getComputedStyle(document.body).backgroundColor;
        if (!isOpaque(bgColor)) bgColor = '#ffffff';

        const curtain = document.createElement('div');
        curtain.className = 'js-stack-carousel-backdrop';
        Object.assign(curtain.style, {
          position: 'sticky',
          top: '0',
          height: '100svh',
          marginBottom: '-100svh',
          backgroundColor: bgColor,
          pointerEvents: 'none',
          display: 'block',
          flexShrink: '0',
        });
        section.prepend(curtain);

        // Pull the NEXT section up by the full margin-bottom so it sits
        // directly below the carousel content with zero gap. The reduction
        // is applied at the start of the next forEach iteration.
        mobileGapReduction = window.innerHeight;

        heroOverlapApplied = true;
      }

      prevWasSelfSticky = section.classList.contains('js-stack-self-sticky');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSectionStack);
  } else {
    initSectionStack();
  }
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((evt) => {
    document.addEventListener(evt, initSectionStack);
  });
  let stackResizeTimer;
  let _stackLastWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    const w = window.innerWidth;
    if (w === _stackLastWidth) return;
    _stackLastWidth = w;
    clearTimeout(stackResizeTimer);
    stackResizeTimer = setTimeout(initSectionStack, 150);
  });
  // ────────────────────────────────────────────────────────────────────────────

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const shouldAnimate = !prefersReducedMotion && window.gsap && window.ScrollTrigger;

  if (!shouldAnimate) return;

  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.config({ ignoreMobileResize: true });

  const sequences = new Map();

  const cleanup = (root = document) => {
    const scoped = root.querySelectorAll('[data-scroll-sequence]');
    scoped.forEach((el) => {
      const ctx = sequences.get(el);
      if (ctx) {
        ctx.revert();
        sequences.delete(el);
      }
    });
  };

  const initSequence = (section) => {
    if (sequences.has(section)) return;

    const sequenceType = section.dataset.scrollSequence;
    const trigger = section.querySelector('[data-scroll-trigger]') || section;

    const ctx = gsap.context(() => {
      switch (sequenceType) {
        case 'banner-overlay': {
          const banner = section.querySelector('[data-scroll-banner]');
          const text = section.querySelector('[data-scroll-text]');
          const image = section.querySelector('[data-scroll-image]');
          const button = section.querySelector('[data-scroll-button]');

          if (!banner || !text || !image || !button) return;

          gsap.set([text, image, button], { autoAlpha: 0 });

          const tl = gsap.timeline({
            defaults: { ease: 'none' },
            scrollTrigger: {
              trigger,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
            },
          });

          tl.fromTo(banner, { autoAlpha: 0.5 }, { autoAlpha: 1, duration: 0.2 })
            .to(text, { autoAlpha: 1, y: 0, duration: 0.2 }, 0.15)
            .to(image, { autoAlpha: 1, y: 0, duration: 0.2 }, 0.35)
            .to(button, { autoAlpha: 1, y: 0, duration: 0.2 }, 0.5);

          ScrollTrigger.create({
            trigger,
            start: 'top top',
            end: '+=120%',
            pin: image,
            pinSpacing: false,
          });

          gsap.to(button, {
            yPercent: -45,
            ease: 'none',
            scrollTrigger: {
              trigger,
              start: 'top center',
              end: 'bottom center',
              scrub: true,
            },
          });
          break;
        }

        case 'image-up': {
          const image = section.querySelector('[data-scroll-image]');
          const text = section.querySelector('[data-scroll-text]');
          if (!image) return;

          if (window.innerWidth < 750) {
            gsap.set(image, { y: 0, clearProps: 'transform' });
            if (text) gsap.set(text, { y: 0, clearProps: 'transform' });
            return;
          }

          gsap.set(image, { y: 0 });
          gsap.to(image, {
            yPercent: -6,
            ease: 'none',
            force3D: true,
            scrollTrigger: {
              trigger,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
            },
          });
          break;
        }

        default:
          break;
      }
    }, section);

    sequences.set(section, ctx);
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-scroll-sequence]').forEach(initSequence);
    ScrollTrigger.refresh();
  };

  document.addEventListener('DOMContentLoaded', () => {
    initAll(document);
  });

  window.addEventListener('load', () => {
    requestAnimationFrame(() => { ScrollTrigger.refresh(); });
  });

  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target;
    cleanup(container);
    initAll(container);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    cleanup(event.target);
    ScrollTrigger.refresh();
  });
})();
